/**
 * Platform timezone used for every user-visible date/time.
 *
 * Dates must be rendered in one explicit IANA zone on both the server (SSR)
 * and the browser. Formatting in the process' local zone made SSR (server on
 * Asia/Riyadh) and the client disagree, which broke hydration, and it would
 * drift again when Egypt leaves UTC+3 for UTC+2 on 2026-10-30.
 *
 * The tenant can override it with `settings.general.timezone`; otherwise the
 * platform default (Africa/Cairo) is used.
 *
 * This module intentionally has no path-alias imports so it can be used by
 * the node:test unit tests.
 */
export const DEFAULT_PLATFORM_TIME_ZONE = "Africa/Cairo";

let platformTimeZone = DEFAULT_PLATFORM_TIME_ZONE;

/**
 * Whether the runtime's Intl implementation recognizes the IANA zone name.
 *
 * @example
 * isValidTimeZone("Africa/Cairo"); // true
 */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Set the tenant timezone (from public settings). Invalid or empty values
 * fall back to the platform default.
 *
 * @example
 * setPlatformTimeZone(settings.general?.timezone);
 */
export function setPlatformTimeZone(timeZone?: string | null): void {
  platformTimeZone = isValidTimeZone(timeZone)
    ? timeZone
    : DEFAULT_PLATFORM_TIME_ZONE;
}

/**
 * The IANA zone every user-visible date should be formatted in.
 *
 * @example
 * new Intl.DateTimeFormat("en", { timeZone: getPlatformTimeZone() });
 */
export function getPlatformTimeZone(): string {
  return platformTimeZone;
}

const englishMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export type ZonedDateParts = {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  /** English long month name, e.g. "October" */
  monthName: string;
  /** English short month name, e.g. "Oct" */
  shortMonthName: string;
};

/**
 * Calendar parts of an instant as seen in the given (default: platform) zone.
 *
 * @example
 * getZonedDateParts(Date.UTC(2026, 9, 29, 22, 30)); // { day: 30, month: 10, ... } in Cairo
 */
export function getZonedDateParts(
  timestamp: number | Date,
  timeZone = getPlatformTimeZone(),
): ZonedDateParts {
  const parts = new Intl.DateTimeFormat("en-US-u-nu-latn", {
    timeZone,
    year: "numeric",
    month: "long",
    day: "numeric",
  }).formatToParts(timestamp);
  const valueOf = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  const monthName = valueOf("month");
  const month = englishMonthNames.indexOf(monthName) + 1;

  return {
    year: Number(valueOf("year")),
    month,
    day: Number(valueOf("day")),
    monthName,
    shortMonthName: monthName.slice(0, 3),
  };
}
